/**
 * Single source of truth for execution-status visuals.
 *
 * Anywhere the UI needs to render an icon, label, or color for an
 * execution status, import from this module instead of hand-rolling
 * a mapping. See #123 for the bug class this prevents.
 */
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Clock,
  Ban,
  Timer,
  SkipForward,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExecutionStatus } from '@/types/connectors';

export interface StatusConfig {
  label: string;
  icon: LucideIcon;
  colorClasses: string;
  bgClasses: string;
  iconAnimation?: string;
}

export const STATUS_CONFIGS: Record<ExecutionStatus, StatusConfig> = {
  pending: {
    label: 'Pending',
    icon: Clock,
    colorClasses: 'text-muted-foreground',
    bgClasses: 'bg-muted border-border',
  },
  running: {
    label: 'Running',
    icon: Loader2,
    colorClasses: 'text-info',
    bgClasses: 'bg-info/10 border-info/30',
    iconAnimation: 'animate-spin',
  },
  success: {
    label: 'Success',
    icon: CheckCircle2,
    colorClasses: 'text-success',
    bgClasses: 'bg-success/10 border-success/30',
  },
  partial: {
    label: 'Partial',
    icon: AlertTriangle,
    colorClasses: 'text-warning',
    bgClasses: 'bg-warning/10 border-warning/30',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    colorClasses: 'text-destructive',
    bgClasses: 'bg-destructive/10 border-destructive/30',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    colorClasses: 'text-muted-foreground',
    bgClasses: 'bg-muted border-border',
  },
  // #1046: the revoke was issued but the worker never confirmed it stopped, so
  // the run may still be writing. Warning-coloured and pulsing rather than the
  // muted grey of a settled 'cancelled' — this state is unfinished business.
  cancelling: {
    label: 'Cancelling',
    icon: Ban,
    colorClasses: 'text-warning',
    bgClasses: 'bg-warning/10 border-warning/30',
    iconAnimation: 'animate-pulse',
  },
  timeout: {
    label: 'Timeout',
    icon: Timer,
    colorClasses: 'text-alert',
    bgClasses: 'bg-alert/10 border-alert/30',
  },
  skipped: {
    label: 'Skipped',
    icon: SkipForward,
    colorClasses: 'text-muted-foreground',
    bgClasses: 'bg-muted border-border',
  },
};

/**
 * Status considered terminal (no more polling, no more progress).
 *
 * 'cancelling' is deliberately absent (#1046): the run has not stopped, so the
 * UI must keep polling until the liveness reconcile promotes it to 'cancelled'.
 */
export const TERMINAL_STATUSES: ReadonlySet<ExecutionStatus> = new Set([
  'success', 'partial', 'failed', 'cancelled', 'timeout', 'skipped',
]);

/**
 * Resolve a status (possibly null/undefined or unknown) to a known config,
 * falling back to 'pending' for unknown values.
 */
export function resolveStatusConfig(status: ExecutionStatus | string | null | undefined): StatusConfig {
  if (status && (STATUS_CONFIGS as any)[status]) {
    return (STATUS_CONFIGS as any)[status];
  }
  return STATUS_CONFIGS.pending;
}

/**
 * StatusIcon — render only the lucide icon for a given status, with the
 * canonical color and animation. No badge wrapper, no label.
 *
 * @example
 * <StatusIcon status="cancelled" size="h-4 w-4" />
 */
export interface StatusIconProps {
  status: ExecutionStatus | string | null | undefined;
  size?: string; // tailwind h-X w-X classes; default h-4 w-4
  className?: string;
}

export function StatusIcon({ status, size = 'h-4 w-4', className }: StatusIconProps) {
  const config = resolveStatusConfig(status);
  const Icon = config.icon;
  return (
    <Icon
      className={cn(size, config.colorClasses, config.iconAnimation, className)}
      aria-hidden="true"
    />
  );
}
